<template>
    <div class="modal-content">
        <div class="modal-header">
            <h5 class="modal-title" id="asset-linked-file-add-form-modal-label">
                {{ $root.text('Attach file to collection', null, true) }}
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" :disabled="updating">
                <span aria-hidden="true">×</span>
            </button>
        </div>
        <div class="modal-body collection-link-file-form">
            <div v-if="updated" class="alert alert-success" role="alert">
                <div class="row">
                    <div class="col-1" style="max-width: 30px"><i class="far fa-check"></i></div>
                    <div class="col" v-html="updated"></div>
                </div>
            </div>
            <div class="row" :style="'display: ' + (!updated ? 'flex' : 'none')">
                <div class="col-md-6 col-12" style="max-height: 450px; overflow-y: auto">
                    <vue-dropzone
                        v-if="dropzone_options"
                        ref="collectionLinkFileDropzone"
                        id="collection-link-file-dropzone"
                        :options="dropzone_options"
                        v-on:vdropzone-sending="onSendingFile"
                        v-on:vdropzone-upload-progress="onProgressUpdate"
                        v-on:vdropzone-success="onFileUploaded"
                        v-on:vdropzone-error="onUploadError"
                        v-on:vdropzone-drag-over="onDragOver"
                        v-on:vdropzone-drag-leave="onDragLeave"
                        v-on:vdropzone-drop="onDragLeave"
                        v-on:vdropzone-file-added="onFileAdded"
                        :useCustomSlot=true
                    >
                        <div v-for="file in uploaded_files" :key="file.id" class="dz-uploaded-file p-2 mb-1">
                            <div class="dz-thumbnail">
                                <span v-if="!file.preview_image">
                                    <img
                                        :src="$root.imagePlaceholder(file.filename)"
                                        class="img-fluid"
                                        alt=""
                                    >
                                </span>
                                <img
                                    v-else
                                    :src="file.preview_image"
                                    class="img-fluid"
                                    alt=""
                                    v-on:mouseover="viewImageIcon(file.id, true)"
                                    v-on:mouseout="viewImageIcon(file.id, false)"
                                >
                                <div
                                    v-if="file.preview_image"
                                    class="view-image-icon"
                                    :id="'view-image-icon' + file.id"
                                    v-b-tooltip.hover
                                    :title="$root.text('Enlarge', null, true)"
                                    @click="viewImage(file)"
                                    v-on:mouseover="viewImageIcon(file.id, true)"
                                    v-on:mouseout="viewImageIcon(file.id, false)"
                                >
                                    <i class="fal fa-expand-arrows"></i>
                                </div>
                            </div>
                            <div class="dz-filename">{{ file.filename }}</div>
                            <div class="dz-size" v-html="$root.formatFileSize(file.file_size)"></div>
                            <div class="dz-remove">
                                <a
                                    v-if="!updating"
                                    href="#"
                                    v-b-tooltip.hover
                                    :title="$root.text('Delete', null, true)"
                                    @click.prevent="removeUploadedFile(file.id)"
                                >
                                    <i class="fal fa-trash-alt"></i>
                                </a>
                                <span v-if="updating"><i class="fal fa-trash-alt"></i></span>
                            </div>
                            <div
                                class="dz-upload-info"
                                :id="'upload-info' + file.id"
                                style="left: 160px"
                            >
                                <i class="fal fa-clock mr-1"></i> {{ $root.text('Pending upload', null, true) }}
                            </div>
                            <div
                                class="dz-progress"
                                :id="'upload-progress' + file.id"
                                style="width: calc(100% - 172px)"
                            >
                                <div class="dz-upload"></div>
                            </div>
                        </div>
                        <a
                            href="#"
                            class="dz-add-files"
                            id="collection-link-file-add-files"
                            @click.prevent="doNothing()"
                            :style="dzAddFilesStyle()"
                        >
                            <i class="far fa-cloud-upload-alt font-size-20 mr-1"></i>
                            {{ $root.text('Click or drop files here to upload', null, true) }}
                        </a>
                    </vue-dropzone>
                </div>
                <div class="col-md-6 col-12 mt-md-0 mt-3">
                    <div v-if="errors" v-html="errors" class="alert alert-danger" role="alert"></div>

                    <form v-if="$root.hasPermission('assets_manage_attachments')" v-on:submit.prevent="onSubmit()">
                        <div class="form-group">
                            <label
                                for="collection-link-file-title"
                                id="collection-link-file-title-label"
                                class="mandatory-label"
                            >{{ $root.text('Title', null, true) }} *</label>
                            <input
                                v-model="title"
                                type="text"
                                id="collection-link-file-title"
                                class="form-control mandatory-field"
                                :readonly="updating"
                                v-on:keyup="$root.unHighlightErrors('collection-link-file-title')"
                                v-on:change="$root.unHighlightErrors('collection-link-file-title')"
                            >
                        </div>
                        <div>
                            <label
                                for="collection-link-file-description"
                                id="collection-link-file-description-label"
                            >{{ $root.text('Description', null, true) }}</label>
                            <input
                                v-model="description"
                                type="text"
                                id="collection-link-file-description"
                                class="form-control"
                                :readonly="updating"
                            >
                        </div>
                    </form>
                </div>
            </div>
        </div>
        <div v-if="$root.hasPermission('assets_manage_attachments')" class="modal-footer" :style="modalFooterStyle()">
            <div v-if="(uploading || updating)" class="progress-display" v-html="uploadProgress()"></div>

            <span class="button-holder" :style="addMoreFilesStyle()">
                <button
                    type="button"
                    class="btn btn-secondary"
                    id="collection-link-file-add-more-files"
                    :disabled="updating"
                >
                    <i class="far fa-cloud-upload-alt mr-1"></i>
                    {{ $root.text('Add more files', null, true) }}
                </button>
            </span>

            <span v-if="!updating">
                <span>
                    <button type="button" class="btn btn-primary" @click="onSubmit()" :disabled="uploading">
                        <i class="far fa-paperclip mr-1"></i> {{ $root.text('Attach', null, true) }}
                    </button>
                </span>
            </span>
            <span v-else>
                <button type="button" class="btn btn-primary">
                    <i class="fal fa-spinner-third fa-spin mr-1"></i>
                    <span style="opacity: 0.5">{{ $root.text('Attach', null, true) }}</span>
                </button>
            </span>
        </div>
    </div>
</template>

<script>
    import vue2Dropzone from 'vue2-dropzone';
    export default {
        components: {
            'vue-dropzone': vue2Dropzone
        },
        data () {
            return {
                errors: false,
                uploading: false,
                updating: false,
                updated: false,
                message: false,
                collection_id: null,
                title: null,
                description: null,
                uploaded_files: [],
                upload_progress: {
                    progress: 0,
                    bytes_total: 0,
                    bytes_sent: 0
                },
                dropzone_options: null
            }
        },
        mounted () {
            var vm = this;
            this.dropzone_options = {
                url: this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-v2',
                createImageThumbnails: true,
                maxThumbnailFilesize: 20,
                thumbnailWidth: 50,
                thumbnailHeight: 50,
                uploadMultiple: false,
                parallelUploads: 1,
                chunking: true,
                forceChunking: true,
                parallelChunkUploads: false,
                chunkSize: 10000000,
                retryChunks: true,
                retryChunksLimit: 5,
                maxFilesize: (10 * 1000 * 1000 * 1000),
                autoProcessQueue: true,
                timeout: 0,
                clickable: '#collection-link-file-add-files, #collection-link-file-add-more-files',
                headers: {
                    'Authorization': 'Bearer ' + this.$root.token
                },
                accept: function (file, done) {
                    if (
                        !file.type
                        && (vm.$root.client.upload_disallowed_file_types.indexOf(file.type.toLowerCase()) > -1)
                    ) {
                        done(
                            {
                                error: vm.$root.text('File type not accepted', null, true),
                                filename: file.name,
                                file_type: file.type
                            }
                        );

                        return null;
                    }
                    const ext = vm.$root.getFileExtension(file.name);
                    if (ext && (vm.$root.client.upload_disallowed_file_extensions.indexOf(ext) > -1)) {
                        done(
                            {
                                error: vm.$root.text('File type not accepted', null, true),
                                filename: file.name,
                                file_type: file.type
                            }
                        );

                        return null;
                    }
                    done();
                }
            };
        },
        methods: {
            resetForm: function (collectionId) {
                if (!this.$root.hasPermission('assets_manage_attachments')) {
                    this.errors = this.$root.text('Sorry, you do not have access to this function.', null, true);

                    return false;
                }
                this.collection_id = collectionId;
                this.title = null;
                this.description = null;
                this.errors = false;
                this.uploading = false;
                this.updating = false;
                this.updated = false;
                this.message = false;
                this.uploaded_files = [];
                this.upload_progress = {
                    progress: 0,
                    bytes_total: 0,
                    bytes_sent: 0
                };
                if (this.$refs.collectionLinkFileDropzone) {
                    this.$refs.collectionLinkFileDropzone.removeAllFiles();
                }
                $('.mandatory-label').removeClass('text-danger');
                $('.mandatory-field').removeClass('field-error');
            },
            onSubmit: function () {
                this.errors = false;
                $('.mandatory-label').removeClass('text-danger');
                $('.mandatory-field').removeClass('field-error');

                if (this.uploaded_files.length == 0) {
                    this.errors = this.$root.text('Please add at least 1 file', null, true);

                    return null;
                }

                if (this.uploading) {
                    this.showPleaseWaitMessage();

                    return null;
                }

                this.message = false;

                /*if (this.filesUploaded() < this.uploaded_files.length) {
                    $('.dz-upload-info').html('<i class="far fa-clock mr-1"></i> Pending upload');
                    this.$refs.collectionLinkFileDropzone.processQueue();
                    this.uploading = true;

                    return null;
                }*/
                this.message = this.$root.text('Attaching file', null, true);
                if (this.uploaded_files.length > 1) {
                    this.message += 's';
                }
                this.linkUploadedFiles();
            },
            showPleaseWaitMessage: function () {
                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.message = {
                    type: null,
                    title: this.$root.text('Attach file(s)', null, true),
                    loading: true,
                    text: '<div style="text-align: center">'
                        + this.$root.text('Please wait until all files are uploaded', null, true)
                        + '</div>'
                };
                $('#message-modal').modal('show');
                $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
            },
            linkUploadedFiles: function () {
                let filesLinked = 0;
                let file = null;
                this.uploaded_files.forEach((item) => {
                    if (item.linked) {
                        filesLinked++;
                    } else if (!file) {
                        file = item;
                    }
                });

                if (filesLinked == this.uploaded_files.length) {
                    // Make current collection active. R2-232
                    if (this.$root.current_collection != this.collection_id) {
                        this.$root.current_collection = this.collection_id;
                    }
                    this.updating = false;
                    if (filesLinked == 1) {
                        this.updated = this.$root.text('1 file attached');
                    } else {
                        this.updated = this.$root.text('{x} files attached', { x: filesLinked });
                    }

                    // Reload collection
                    const component = this.$root.findComponent(this.$parent, 'view-collection');
                    if (component) {
                        component.getCollection(this.collection_id);
                    }

                    // Close modal automatically after 2 seconds
                    setTimeout(function () {
                        $('#collection-linked-file-add-form-modal').modal('hide');
                    }, 2000);

                    return null;
                }

                if (filesLinked > 0) {
                    this.message = this.$root.text(
                        'File {x} of {y} attached',
                        { x: filesLinked, y: this.uploaded_files.length },
                        true
                    );
                }

                this.updating = true;
                const path = '/' + this.$root.app.client_id + '/collection/' + this.collection_id + '/linked-file';
                const url = this.$root.app.api_url + path;
                const data = {
                    title: this.title,
                    description: this.description,
                    id: file.id,
                    filename: file.filename,
                    file_type: file.file_type,
                    file_size: file.file_size,
                    temp_file: file.temp_file
                };
                var vm = this;

                axios({
                    method: 'post',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.uploaded_files.forEach((item, index) => {
                        if (item.id == response.data.file_id) {
                            item.linked = true;
                        }
                    });
                    vm.linkUploadedFiles();
                })
                .catch(function (error) {
                    vm.updating = false;
                    vm.errors = vm.$root.text('Something went wrong, please try again', null, true);
                    $('.collection-link-file-form')[0].scrollTop='0px';
                    if (error.response) {
                        if (error.response.status == 401) {
                            // missing/lost API token - close modal and logout user
                            $('#asset-linked-file-add-form-modal').modal('hide');
                            vm.$root.doLogout();
                        }
                        if (error.response.data.error) {
                            if (typeof error.response.data.error === 'string') {
                                vm.errors = error.response.data.error;
                            } else if (typeof error.response.data.error === 'object') {
                                vm.errors = '';
                                for (const [key, value] of Object.entries(error.response.data.error)) {
                                    vm.errors += value.join('<br>') + '<br>';
                                    $('#collection-link-file-' + key + '-label').addClass('text-danger');
                                    $('#collection-link-file-' + key).addClass('field-error');
                                }
                                setTimeout(function () {
                                    const dropzoneHeight = $('#collection-link-file-dropzone').outerHeight();
                                    $('.collection-link-file-form').animate({ scrollTop: dropzoneHeight }, 700);
                                }, 1000);
                            }
                        }
                    }
                });
            },
            filesUploaded: function () {
                let count = 0;
                this.uploaded_files.forEach((file) => {
                    if (file.temp_file) {
                        count++;
                    }
                });

                return count;
            },
            doNothing: function () {
                return false;
            },
            onSendingFile: function (file, xhr, formData) {
                formData.append('id', this.$root.getUploadFileId(file));
            },
            onProgressUpdate: function (file, progress, bytesSent) {
                if ((this.uploaded_files.length == 0) || (progress > 99.9)) {
                    return null;
                }
                let bytesTotal = 0;
                let bytesSentTotal = 0;
                this.uploaded_files.forEach((item) => {
                    bytesTotal += item.file_size;
                    if (item.id == file.upload.uuid) {
                        if (isNaN(bytesSent)) {
                            bytesSent = file.size * (progress / 100);
                        }
                        item.upload.progress = progress;
                        item.upload.bytes_sent = bytesSent;
                        bytesSentTotal += bytesSent;
                        $('#upload-info' + item.id).html(
                            this.$root.text('Uploading', null, true) + ' ' + this.$root.formatFileSize(bytesSent)
                                + ' ' + progress.toFixed(1) + '%'
                        );
                        $('#upload-progress' + item.id + ' > div').css('width', progress + '%');
                    } else if (item.upload.progress == 100) {
                        bytesSentTotal += item.file_size;
                    }
                });
                this.upload_progress.progress = ((bytesTotal * bytesSentTotal) > 0)
                    ? ((bytesSentTotal / bytesTotal) * 100) : 0;
                this.upload_progress.bytes_total = bytesTotal;
                this.upload_progress.bytes_sent = bytesSentTotal;
            },
            onFileUploaded: function (file, response) {
                if (this.uploaded_files.length == 0) {
                    return null;
                }
                // Find file.
                let itemIndex = -1;
                this.uploaded_files.forEach((item, index) => {
                    if (item.id == file.upload.uuid) {
                        itemIndex = index;
                    }
                });

                if (itemIndex > -1) {
                    this.uploaded_files[itemIndex].upload.progress = 100;
                    this.uploaded_files[itemIndex].upload.bytes_sent = file.size;
                    $('#upload-progress' + file.upload.uuid + ' > div').css('width', '100%');
                    if (!this.uploaded_files[itemIndex].preview_image) {
                        this.uploaded_files[itemIndex].preview_image = (file.dataURL || null);
                    }
                    $('#upload-info' + file.upload.uuid).html(
                        this.$root.text(
                            this.uploaded_files[itemIndex].preview_image ? 'Finalising upload...' : 'Previewing...',
                            null,
                            true
                        )
                    );
                    this.getUploadInfo(this.uploaded_files[itemIndex].upload_id);
                    this.$refs.collectionLinkFileDropzone.removeFile(file);
                }
            },
            getUploadInfo: function (fileId) {
                /**
                 * Send request to API
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-info/' + fileId
                    + '/preview/500x500';
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    // Find file.
                    let itemIndex = -1;
                    vm.uploaded_files.forEach((item, index) => {
                        if (item.upload_id == response.data.file_id) {
                            itemIndex = index;
                        }
                    });
                    if (itemIndex < 0) {
                        return null;
                    }
                    vm.uploaded_files[itemIndex].temp_file = response.data.temp_file;
                    vm.onUploadComplete();
                    $('#upload-info' + vm.uploaded_files[itemIndex].id).html('');

                    if (response.data.preview) {
                        vm.uploaded_files[itemIndex].preview_image = response.data.preview.src;
                    }
                })
                .catch(function (error) {
                    // Do anything?
                });
            },
            onUploadComplete: function () {
                let allUploaded = true;
                this.uploaded_files.forEach((item) => {
                    if (!item.temp_file) {
                        allUploaded = false;
                    }
                });
                if (!allUploaded) {
                    return null;
                }
                this.uploading = false;
                this.upload_progress.progress = 0;
                if ($('#message-modal').hasClass('show')) {
                    $('#message-modal').modal('hide');
                    const appComponent = this.$root.findComponent(this.$root, 'main-app');
                    appComponent.resetModalBackdrop();
                }
                //this.onSubmit();
            },
            onUploadError: function (file, message, xhr) {
                if (file.status != 'canceled') {
                    if (message.error) {
                        this.errors = message.error + ' ' + message.filename + ' (' + message.file_type + ')';
                    } else {
                        this.errors = this.$root.text('Something went wrong, please try again', null, true);
                    }
                }
                this.removeUploadedFile(file.upload.uuid, true);
                this.onUploadComplete();
                console.log(file, message);
            },
            onDragOver: function (event) {
                $('#collection-link-file-dropzone').addClass('dz-drag-highlight');
            },
            onDragLeave: function (event) {
                $('#collection-link-file-dropzone').removeClass('dz-drag-highlight');
            },
            onFileAdded: function (file) {
                if (this.updating) {
                    this.$refs.collectionLinkFileDropzone.removeFile(file);

                    return null;
                }

                // Check file size.
                if (file.size > this.dropzone_options.maxFilesize) {
                    this.$refs.collectionLinkFileDropzone.removeFile(file);
                    const appComponent = this.$root.findComponent(this.$root, 'main-app');
                    appComponent.message = {
                        type: 'error',
                        title: this.$root.text('Maximum upload size exceeded', null, true),
                        text: this.$root.text('The maximum size of each file you can upload is {size}.',
                            { size: this.$root.formatFileSize(this.dropzone_options.maxFilesize) }, true) + ' '
                            + this.$root.text('The size of {filename} is {size}.', { filename: file.name,
                            size: this.$root.formatFileSize(file.size) }, true)
                    };
                    $('#message-modal').modal('show');
                    $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });

                    return null;
                }

                let newFile = {
                    id: file.upload.uuid,
                    upload_id: this.$root.getUploadFileId(file),
                    filename: file.name,
                    file_type: file.type,
                    file_size: file.size,
                    upload: {
                        progress: 0,
                        bytes_sent: 0
                    },
                    preview_image: (file.dataURL || null)
                };
                this.uploaded_files.push(newFile);
                this.uploading = true;
                this.errors = false;
                var vm = this;
                setTimeout(function () {
                    $('#upload-info' + file.upload.uuid).html(vm.$root.text('Preparing upload...', null, true));
                }, 500);
            },
            dzAddFilesStyle: function () {
                if (this.uploaded_files.length == 0) {
                    return 'display: block';
                }

                return 'display: none';
            },
            addMoreFilesStyle: function () {
                if (!this.uploading && !this.updating && (this.uploaded_files.length > 0)) {
                    return 'display: block';
                }

                return 'display: none';
            },
            modalFooterStyle: function () {
                if (!this.updated) {
                    return 'display: flex';
                }

                return 'display: none';
            },
            uploadProgress: function () {
                if (this.message) {
                    return this.message;
                }
                const percent = this.upload_progress.progress.toFixed(1);

                return this.$root.text('Uploading', null, true) + ' ' + ((percent < 100) ? percent : '100') + '%';
            },
            viewImage: function (file) {
                this.viewImageIcon(file.id, false);

                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.message = {
                    type: null,
                    title: file.filename,
                    text: '<div class="text-center"><img src="' + file.preview_image
                        + '" alt="" class="img-fluid" style="max-height: 500px"></div>'
                };
                $('#message-modal > div').addClass('modal-lg');
                $('#message-modal').modal('show');
                $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
            },
            viewImageIcon: function (fileId, show) {
                if (show) {
                    $('#view-image-icon' + fileId).show();
                } else {
                    $('#view-image-icon' + fileId).hide();
                }
            },
            removeUploadedFile: function(fileId, confirmed = false) {
                let fileIndex = -1;
                this.uploaded_files.forEach((item, index) => {
                    if (item.id == fileId) {
                        fileIndex = index;
                    }
                });
                if (fileIndex > -1) {
                    if (confirmed) {
                        const removeUploadFileId = (this.uploaded_files[fileIndex].upload.progress < 100) ? fileId
                            : null;

                        this.uploaded_files.splice(fileIndex, 1);

                        if (removeUploadFileId) {
                            this.$refs.collectionLinkFileDropzone.getUploadingFiles().forEach((file) => {
                                if (file.upload.uuid == removeUploadFileId) {
                                    this.$refs.collectionLinkFileDropzone.removeFile(file);
                                }
                            });
                            this.$refs.collectionLinkFileDropzone.getQueuedFiles().forEach((file) => {
                                if (file.upload.uuid == removeUploadFileId) {
                                    this.$refs.collectionLinkFileDropzone.removeFile(file);
                                }
                            });
                        }
                    
                        return null;
                    }

                    const appComponent = this.$root.findComponent(this.$root, 'main-app');
                    appComponent.confirm_delete = {
                        title: this.$root.text('Delete file', null, true),
                        text: this.$root.text(
                            'Are you sure you want to delete: {filename}?',
                            { filename: this.uploaded_files[fileIndex].filename },
                            true
                        ),
                        component: this,
                        action: 'delete-file-' + fileId
                    };
                    $('#confirm-delete-modal').modal('show');
                    $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
                }
            },
            confirmDelete: function (action) {
                if (action.indexOf('delete-file-') > -1) {
                    const fileId = action.substr(12);
                    this.removeUploadedFile(fileId, true);
                }
            }
        }
    }
</script>
<style scoped>
    #collection-link-file-dropzone {
        min-height: 138px;
    }

    #collection-link-file-add-files {
        padding-top: 55px;
    }

    .dz-uploaded-file {
        cursor: default;
    }

    #collection-link-file-add-more-files {
        position: absolute;
        left: 15px;
        bottom: 16px;
    }

    .progress-display {
        position: absolute;
        left: 15px;
        bottom: 10px;
        width: calc(100% - 100px);
    }
</style>
